import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { FloatingNotchMetrics } from '../utils';
import {
  isSameFloatingNotch,
  measureFloatingNotch,
  setNotchReveal,
  subscribeFloatingNotchMeasure,
} from '../utils';

interface UseFloatingNotchReturn {
  notch: FloatingNotchMetrics | null;
  tabBarRef: React.RefObject<HTMLDivElement>;
}

export const useFloatingNotch = (
  enabled: boolean,
  dependencyKey?: string,
): UseFloatingNotchReturn => {
  const tabBarRef = useRef<HTMLDivElement>(null);
  const [notch, setNotch] = useState<FloatingNotchMetrics | null>(null);

  useLayoutEffect(() => {
    setNotchReveal(enabled);

    return () => {
      setNotchReveal(false);
    };
  }, [enabled]);

  useEffect(() => {
    const tabBar = tabBarRef.current;

    if (!tabBar || !enabled) {
      setNotch(null);

      return undefined;
    }

    const measure = () => {
      const next = measureFloatingNotch(tabBar);

      setNotch(prev => {
        if (!next) {
          return null;
        }

        return isSameFloatingNotch(prev, next) ? prev : next;
      });
    };

    const unsubscribe = subscribeFloatingNotchMeasure(tabBar, measure);

    return () => {
      unsubscribe();
    };
  }, [enabled, dependencyKey]);

  return { notch, tabBarRef };
};

export default useFloatingNotch;
